import { memo, useMemo } from 'react';

import PropTypes from 'prop-types';

import { cn } from '@/shadcn/lib/utils';

import {
  getAggregationConfig as getWidgetAggregationConfig,
  Widget
} from '@/components/ui/widget';

// get the relevant aggregation config to apply
export const getAggregationConfig = colConfig =>
  colConfig.def?.aggregation || // complete custom aggregation directly defined in the workflow
  getWidgetAggregationConfig(colConfig.def?.cell || {}); // computed aggregation provided by the cell widget given an aggregation key and function defined in the workflow

// table aggregation row
export const AggregationRow = memo(
  ({ height, selectBoxWidth = 0, columns, data }) => {
    const aggregationData = useMemo(
      () => ({
        data
      }),
      [data]
    );
    const aggregationColumns = [];
    columns.forEach(column => {
      const aggregationKey = column.def.cell.aggregation?.aggregationKey;
      if (aggregationKey && aggregationColumns.indexOf(aggregationKey) === -1) {
        aggregationColumns.push(aggregationKey);
      }
    });
    return (
      <div className="flex w-fit flex-row flex-nowrap items-center text-muted-foreground text-xs">
        {selectBoxWidth > 0 && (
          <div
            className="flex items-center justify-center border-border border-r"
            style={{
              width: selectBoxWidth,
              height
            }}
          >
            {data.length}
          </div>
        )}
        {columns.map((colConfig, index) => {
          const { width } = colConfig;
          const aggregations = [];
          if (colConfig.def?.aggregation) {
            // The aggregation config is already completely defined
            // in the workflow config and can be injected directly
            // in the widget

            aggregations.push(getAggregationConfig(colConfig));
          } else {
            // The aggregation config will be computed

            const newColConfig = JSON.parse(JSON.stringify(colConfig));

            // If the column is of type 'text' but can be edited as a number,
            // this means that we can convert back the cells in the 'number'
            // type so that we can compute the aggregated values for that
            // column.

            if (
              Object.hasOwn(colConfig.def.cell.view, 'editor') &&
              colConfig.def.cell.view.type === 'text' &&
              colConfig.def.cell.view.editor.type === 'number'
            ) {
              newColConfig.def.cell.view.type = 'number';
            }

            const aggregationKey =
              newColConfig.def.cell.aggregation?.aggregationKey;
            const aggregationFunction =
              newColConfig.def.cell.aggregation?.aggregationFunction;

            if (aggregationKey) {
              // Given the aggregation key, for eg the 'currency' column,
              // we compute all the unique values of that aggregation key
              // that will be used to group lines together

              const aggregationValues = [
                ...new Set(
                  aggregationData.data.map(item => item[aggregationKey])
                )
              ];

              // For each group of lines, for eg for each currency, we
              // prepare the configuration that will be used in the number
              // widget to compute the value we want (for eg sum by currency,
              // avg by currency, etc)

              aggregationValues.forEach(value => {
                const aggregatedColConfig = {
                  def: {
                    cell: {
                      view: {
                        ...newColConfig.def.cell.view
                      },
                      aggregation: {
                        aggregationKey,
                        aggregationValue: value,
                        aggregationFunction
                      }
                    }
                  }
                };
                aggregations.push(getAggregationConfig(aggregatedColConfig));
              });
            } else if (aggregationColumns.includes(colConfig.key)) {
              const aggregationValues = [
                ...new Set(
                  aggregationData.data.map(item => item[colConfig.key])
                )
              ];
              aggregationValues.forEach(value => {
                const aggregatedColConfig = {
                  def: {
                    cell: {
                      view: {
                        ...newColConfig.def.cell.view
                      },
                      aggregation: {
                        aggregationValue: value
                      }
                    }
                  }
                };
                aggregations.push(getAggregationConfig(aggregatedColConfig));
              });
            } else {
              aggregations.push(getAggregationConfig(newColConfig));
            }
          }

          return (
            <div key={index}>
              {aggregations.map((aggregation, aggregationIndex) => (
                <div
                  className={cn(
                    !!aggregation && 'bg-primary/50 text-primary-foreground'
                  )}
                  data-is-computed={!!aggregation}
                  key={aggregationIndex}
                  style={{ width, height }}
                >
                  <Widget
                    className={cn(
                      'box-border flex size-full cursor-default flex-row flex-wrap items-center overflow-hidden border-border border-r px-1.5 py-0.75',
                      'data-[is-hoverable=true]:cursor-pointer data-[is-hoverable=true]:hover:rounded-sm data-[is-hoverable=true]:hover:border-2 data-[is-hoverable=true]:hover:border-primary data-[is-hoverable=true]:hover:px-1.0 data-[is-hoverable=true]:hover:py-0.25'
                    )}
                    data={aggregationData}
                    widget={aggregation || {}}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }
);

AggregationRow.propTypes = {
  height: PropTypes.number.isRequired,
  selectBoxWidth: PropTypes.number,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired
};
